/**
 * TeamDocumentStore — the Document Bank feature's data-access wrapper.
 *
 * Strict layering: this is the ONLY documents module that talks to the API, and
 * it does so exclusively through the {@link EngineeringVectorApi} facade
 * (`api.documents<TeamDocument>('teamDocuments')` + `api.identityProvider`). It
 * never imports ApiClient or calls fetch. React hooks call this store; pages
 * call the hook.
 *
 * Persistence model: each record is one document with id `doc_<uuid>` in the
 * real 'teamDocuments' generic-store collection (DDB pointer row + S3 body). We
 * store the external link + metadata ONLY (never the document content), so
 * every field is short and every field is mirrored onto the DDB pointer row's
 * index attributes; {@link listSummaries} reconstructs full document summaries
 * directly from the pointer rows WITHOUT hydrating S3 bodies — which is what
 * lets the client-side discovery filters + semantic-ish search run over the
 * whole bank cheaply.
 *
 * Supersede chain: when a document declares it supersedes another, the store
 * maintains the reverse `supersededById` back-link on the target document so
 * both ends of the chain are navigable. Best-effort — a missing target is
 * skipped, never fatal.
 *
 * Deletion: the API layer exposes no delete op, so {@link softDelete} sets a
 * `deleted` flag on the document and its pointer row; queries filter it out.
 *
 * NO backend changes are required: this reuses the generic document store.
 */

import type { EngineeringVectorApi } from '../../api';
import type { DocumentStore } from '../../api';
import type { StringMap } from '../../api';
import type {
  DocumentStatus,
  TeamDocument,
  TeamDocumentDraft,
  TeamDocumentSummary,
} from './types';
import { documentDocId, isDocumentStatus } from './documentModel';

export const TEAM_DOCUMENTS_COLLECTION = 'teamDocuments';

/** Delimiter used to mirror string[] fields onto a single pointer-row attribute. */
const LIST_SEP = '\n';

const joinList = (list?: string[]): string => (list ?? []).join(LIST_SEP);
const splitList = (raw?: string): string[] =>
  (raw ?? '')
    .split(LIST_SEP)
    .map((s) => s.trim())
    .filter(Boolean);

/** Pointer-row index attribute keys. */
const ROW = {
  primaryIdentifier: 'primaryIdentifier',
  collection: 'collection',
  title: 'title',
  docType: 'docType',
  summary: 'summary',
  contextWhy: 'contextWhy',
  authors: 'authors',
  ownerPoc: 'ownerPoc',
  reviewers: 'reviewers',
  reviewedWith: 'reviewedWith',
  status: 'status',
  createdDate: 'createdDate',
  lastReviewedDate: 'lastReviewedDate',
  documentLink: 'documentLink',
  relatedServices: 'relatedServices',
  relatedRepo: 'relatedRepo',
  relatedGoalId: 'relatedGoalId',
  relatedWorkstreamId: 'relatedWorkstreamId',
  relatedStoryIds: 'relatedStoryIds',
  relatedDecisionIds: 'relatedDecisionIds',
  tags: 'tags',
  supersedesId: 'supersedesId',
  supersededById: 'supersededById',
  createdBy: 'createdBy',
  createdAt: 'createdAt',
  lastUpdated: 'lastUpdated',
  deleted: 'deleted',
} as const;

export class TeamDocumentStore {
  private readonly docs: DocumentStore<TeamDocument>;

  constructor(private readonly api: EngineeringVectorApi) {
    this.docs = api.documents<TeamDocument>(TEAM_DOCUMENTS_COLLECTION);
  }

  /** Builds the queryable index attributes mirrored onto the pointer row. */
  private indexAttributes(doc: TeamDocument): StringMap {
    return {
      [ROW.title]: doc.title ?? '',
      [ROW.docType]: doc.docType ?? '',
      [ROW.summary]: doc.summary ?? '',
      [ROW.contextWhy]: doc.contextWhy ?? '',
      [ROW.authors]: joinList(doc.authors),
      [ROW.ownerPoc]: doc.ownerPoc ?? '',
      [ROW.reviewers]: joinList(doc.reviewers),
      [ROW.reviewedWith]: joinList(doc.reviewedWith),
      [ROW.status]: doc.status,
      [ROW.createdDate]: doc.createdDate ?? '',
      [ROW.lastReviewedDate]: doc.lastReviewedDate ?? '',
      [ROW.documentLink]: doc.documentLink ?? '',
      [ROW.relatedServices]: joinList(doc.relatedServices),
      [ROW.relatedRepo]: doc.relatedRepo ?? '',
      [ROW.relatedGoalId]: doc.relatedGoalId ?? '',
      [ROW.relatedWorkstreamId]: doc.relatedWorkstreamId ?? '',
      [ROW.relatedStoryIds]: joinList(doc.relatedStoryIds),
      [ROW.relatedDecisionIds]: joinList(doc.relatedDecisionIds),
      [ROW.tags]: joinList(doc.tags),
      [ROW.supersedesId]: doc.supersedesId ?? '',
      [ROW.supersededById]: doc.supersededById ?? '',
      [ROW.createdBy]: doc.createdBy ?? '',
      [ROW.createdAt]: doc.createdAt ?? '',
      [ROW.lastUpdated]: doc.lastUpdated ?? '',
      [ROW.deleted]: doc.deleted ? 'true' : 'false',
    };
  }

  /** Reconstructs a full document summary from a pointer row. */
  private toSummary(row: StringMap): TeamDocumentSummary {
    const status = row[ROW.status] ?? '';
    return {
      id: row[ROW.primaryIdentifier] ?? '',
      title: row[ROW.title] ?? '',
      docType: row[ROW.docType] ?? '',
      summary: row[ROW.summary] ?? '',
      contextWhy: row[ROW.contextWhy] ?? '',
      authors: splitList(row[ROW.authors]),
      ownerPoc: row[ROW.ownerPoc] ?? '',
      reviewers: splitList(row[ROW.reviewers]),
      reviewedWith: splitList(row[ROW.reviewedWith]),
      status: isDocumentStatus(status) ? status : 'DRAFT',
      createdDate: row[ROW.createdDate] ?? '',
      lastReviewedDate: row[ROW.lastReviewedDate] || undefined,
      documentLink: row[ROW.documentLink] ?? '',
      relatedServices: splitList(row[ROW.relatedServices]),
      relatedRepo: row[ROW.relatedRepo] || undefined,
      relatedGoalId: row[ROW.relatedGoalId] || undefined,
      relatedWorkstreamId: row[ROW.relatedWorkstreamId] || undefined,
      relatedStoryIds: splitList(row[ROW.relatedStoryIds]),
      relatedDecisionIds: splitList(row[ROW.relatedDecisionIds]),
      tags: splitList(row[ROW.tags]),
      supersedesId: row[ROW.supersedesId] || undefined,
      supersededById: row[ROW.supersededById] || undefined,
      createdBy: row[ROW.createdBy] ?? '',
      createdAt: row[ROW.createdAt] ?? '',
      lastUpdated: row[ROW.lastUpdated] ?? '',
    };
  }

  /** Lists all non-deleted document summaries (defensively scoped to this collection). */
  async listSummaries(): Promise<TeamDocumentSummary[]> {
    const rows = await this.docs.list();
    return rows
      .filter(
        (r) =>
          r[ROW.collection] === TEAM_DOCUMENTS_COLLECTION &&
          r[ROW.deleted] !== 'true' &&
          !!r[ROW.primaryIdentifier],
      )
      .map((r) => this.toSummary(r));
  }

  /** Loads a single document record by id (hydrates the S3 body). */
  async get(id: string): Promise<TeamDocument | undefined> {
    return this.docs.get(id);
  }

  /**
   * Registers a new document. Seeds id, createdBy (from identity), createdAt and
   * lastUpdated, persists it, maintains the supersede back-link, and returns the
   * saved record.
   */
  async create(draft: TeamDocumentDraft): Promise<TeamDocument> {
    const identity = await this.api.identityProvider.getIdentity();
    const doc = this.fromDraft(documentDocId(), draft, identity.alias, undefined, undefined);
    await this.docs.save(doc.id, doc, this.indexAttributes(doc));
    await this.maintainSupersedeLink(doc.id, undefined, doc.supersedesId);
    return doc;
  }

  /**
   * Applies a draft's fields to an existing document in place (same id/createdBy/
   * createdAt), refreshes lastUpdated, preserves the supersededById back-link,
   * re-mirrors index attributes, and reconciles the supersede chain.
   */
  async applyDraft(doc: TeamDocument, draft: TeamDocumentDraft): Promise<TeamDocument> {
    const prevSupersedes = doc.supersedesId;
    const updated = this.fromDraft(
      doc.id,
      draft,
      doc.createdBy,
      doc.createdAt,
      doc.supersededById,
    );
    const saved = await this.update(updated);
    await this.maintainSupersedeLink(doc.id, prevSupersedes, saved.supersedesId);
    return saved;
  }

  /** Persists a full document record in place (refreshes lastUpdated). */
  async update(doc: TeamDocument): Promise<TeamDocument> {
    const updated: TeamDocument = {
      ...doc,
      lastUpdated: new Date().toISOString(),
      deleted: false,
    };
    await this.docs.update(updated.id, updated, this.indexAttributes(updated));
    return updated;
  }

  /** Convenience: change only the status of a document. No-op-safe. */
  async setStatus(id: string, status: DocumentStatus): Promise<TeamDocument | undefined> {
    const existing = await this.docs.get(id);
    if (!existing) return undefined;
    if (existing.status === status) return existing;
    return this.update({ ...existing, status });
  }

  /**
   * Soft-deletes a document: fetches it, flags it deleted, updates both the S3
   * body and the pointer row, and clears the supersede back-link on any target.
   */
  async softDelete(id: string): Promise<void> {
    const existing = await this.docs.get(id);
    if (!existing) return;
    const deleted: TeamDocument = { ...existing, deleted: true };
    await this.docs.update(id, deleted, this.indexAttributes(deleted));
    await this.maintainSupersedeLink(id, existing.supersedesId, undefined);
  }

  /**
   * Reconciles the reverse `supersededById` back-link when a document's
   * `supersedesId` changes from `prev` to `next`. Clears the old target's
   * back-link and sets the new target's. Best-effort: a missing/deleted target
   * is skipped.
   */
  private async maintainSupersedeLink(
    superseding: string,
    prev: string | undefined,
    next: string | undefined,
  ): Promise<void> {
    if (prev === next) return;
    if (prev) await this.setSupersededBy(prev, superseding, undefined);
    if (next) await this.setSupersededBy(next, undefined, superseding);
  }

  /**
   * Sets `supersededById` on `targetId` to `to`, but only when it currently
   * equals `expected` (guards against clobbering a newer chain). Skips missing
   * targets.
   */
  private async setSupersededBy(
    targetId: string,
    expected: string | undefined,
    to: string | undefined,
  ): Promise<void> {
    const target = await this.docs.get(targetId);
    if (!target || target.deleted) return;
    if (expected !== undefined && target.supersededById !== expected) return;
    if (target.supersededById === to) return;
    const updated: TeamDocument = {
      ...target,
      supersededById: to,
      lastUpdated: new Date().toISOString(),
    };
    await this.docs.update(updated.id, updated, this.indexAttributes(updated));
  }

  /**
   * Builds a full TeamDocument from a draft, seeding id/createdBy and preserving
   * createdAt + supersededById on edit. Empty optional text fields collapse to
   * undefined.
   */
  private fromDraft(
    id: string,
    draft: TeamDocumentDraft,
    createdBy: string,
    existingCreatedAt: string | undefined,
    supersededById: string | undefined,
  ): TeamDocument {
    const now = new Date().toISOString();
    const clean = (s?: string) => {
      const t = (s ?? '').trim();
      return t ? t : undefined;
    };
    return {
      id,
      title: draft.title.trim(),
      docType: draft.docType.trim(),
      summary: draft.summary.trim(),
      contextWhy: draft.contextWhy.trim(),
      authors: draft.authors ?? [],
      ownerPoc: draft.ownerPoc.trim(),
      reviewers: draft.reviewers ?? [],
      reviewedWith: draft.reviewedWith ?? [],
      status: isDocumentStatus(draft.status) ? draft.status : 'DRAFT',
      createdDate: draft.createdDate.trim(),
      lastReviewedDate: clean(draft.lastReviewedDate),
      documentLink: draft.documentLink.trim(),
      relatedServices: draft.relatedServices ?? [],
      relatedRepo: clean(draft.relatedRepo),
      relatedGoalId: draft.relatedGoalId || undefined,
      relatedWorkstreamId: draft.relatedWorkstreamId || undefined,
      relatedStoryIds: draft.relatedStoryIds ?? [],
      relatedDecisionIds: draft.relatedDecisionIds ?? [],
      tags: draft.tags ?? [],
      supersedesId: draft.supersedesId || undefined,
      supersededById,
      createdBy,
      createdAt: existingCreatedAt ?? now,
      lastUpdated: now,
      deleted: false,
    };
  }
}
